import React, { useState, useEffect } from 'react';
import marketplaceActionTypesService from '../services/marketplaceActionTypesService';
import ConfirmModal from './shared/ConfirmModal';
import './Admin.css';
import './ConnectorForm.css';

const EMPTY = { code: '', category: '', description: '' };

const MarketplaceActionTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newType, setNewType] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(EMPTY);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await marketplaceActionTypesService.list();
      setTypes(res.actionTypes || []);
      setError(null);
    } catch (err) {
      setError('Failed to load action types: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Existing categories power the datalist suggestions (free-text so new ones can be added).
  const categories = Array.from(new Set(types.map(t => t.category).filter(Boolean))).sort();

  const add = async () => {
    if (!newType.code.trim()) return;
    try {
      await marketplaceActionTypesService.create(newType);
      setNewType(EMPTY);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add action type');
    }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditRow({ code: t.code, category: t.category || '', description: t.description || '' });
  };

  const saveEdit = async () => {
    try {
      await marketplaceActionTypesService.update(editId, editRow);
      setEditId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update action type');
    }
  };

  const doDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await marketplaceActionTypesService.remove(id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete action type');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Marketplace Action Types</h2>
      </div>
      <p style={{ color: '#6b7280', marginTop: '-0.5rem', marginBottom: '1rem' }}>
        Managed list of action codes (grouped by category) used by marketplace actions.
      </p>

      {error && <div className="error">{error}</div>}

      <datalist id="action-type-categories">
        {categories.map(c => <option key={c} value={c} />)}
      </datalist>

      <div className="inline-add-row">
        <div className="field">
          <label>Code</label>
          <input type="text" value={newType.code} onChange={e => setNewType(p => ({ ...p, code: e.target.value }))} placeholder="e.g. PUBLISH" />
        </div>
        <div className="field">
          <label>Category</label>
          <input list="action-type-categories" value={newType.category} onChange={e => setNewType(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Publish / Send" />
        </div>
        <div className="field grow">
          <label>Description</label>
          <input type="text" value={newType.description} onChange={e => setNewType(p => ({ ...p, description: e.target.value }))} placeholder="Optional" />
        </div>
        <button type="button" className="btn-primary" onClick={add}>Add</button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Code</th><th>Category</th><th>Description</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {types.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign: 'center' }}>No action types</td></tr>
            ) : types.map(t => (
              <tr key={t.id}>
                {editId === t.id ? (
                  <>
                    <td><input value={editRow.code} onChange={e => setEditRow(p => ({ ...p, code: e.target.value }))} /></td>
                    <td><input list="action-type-categories" value={editRow.category} onChange={e => setEditRow(p => ({ ...p, category: e.target.value }))} /></td>
                    <td><input value={editRow.description} onChange={e => setEditRow(p => ({ ...p, description: e.target.value }))} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn-small btn-primary" onClick={saveEdit}>Save</button>
                      <button type="button" className="btn-small" style={{ marginLeft: 4 }} onClick={() => setEditId(null)}>Cancel</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ fontWeight: 600 }}>{t.code}</td>
                    <td>{t.category || '-'}</td>
                    <td>{t.description || '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn-small" onClick={() => startEdit(t)} title="Edit">✏️</button>
                      <button type="button" className="btn-small" style={{ marginLeft: 4, color: '#dc2626' }} onClick={() => setConfirmDeleteId(t.id)} title="Delete">🗑️</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmDeleteId !== null && (
        <ConfirmModal
          title="Delete action type"
          message="Delete this action type? It cannot be deleted if any marketplace action still uses it."
          confirmLabel="Delete"
          danger
          onConfirm={doDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};

export default MarketplaceActionTypes;
